import React from 'react';
import SylvaWordmark from "/sylva-wordmark.png";

/** Brand hero background for all auth screens — a calm purple wash with a
 *  touch of depth, echoing the immersive single-surface login feel.
 *  The form floats directly on it (no card). */
export const AUTH_HERO_GRADIENT =
  'linear-gradient(155deg, hsl(279 50% 38%) 0%, hsl(279 54% 31%) 48%, hsl(279 58% 22%) 100%)';

/** Shared field styling so every auth screen reads on the purple hero:
 *  translucent inputs, light labels, a light pill submit button. */
export const AUTH_INPUT =
  'bg-white/[0.08] border-white/15 text-white placeholder:text-white/40 focus-visible:border-white/40 focus-visible:ring-white/20 focus-visible:ring-offset-0';
export const AUTH_LABEL = 'text-white/75';
export const AUTH_SUBMIT =
  'w-full rounded-full bg-white text-primary hover:bg-white/90 focus-visible:ring-white/30 focus-visible:ring-offset-0';

/** Ultra-fine film grain so the flat purple doesn't read as a plastic fill. */
const GRAIN_URL =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2'/></filter><rect width='140' height='140' filter='url(%23n)' opacity='0.7'/></svg>\")";

interface AuthShellProps {
  /** Small line under the wordmark (optional). */
  tagline?: string;
  children: React.ReactNode;
}

export const AuthShell: React.FC<AuthShellProps> = ({ tagline, children }) => (
  // The app shell locks html/body/#root to height:100% + overflow:hidden, so a
  // normal-flow page can't scroll. We escape that with a viewport-anchored
  // scroll container (fixed inset-0 + overflow-y-auto) — immune to the shell's
  // overflow:hidden. Centering uses margin:auto on the inner block (NOT
  // justify-center, which clips the top when the form is taller than the
  // viewport): it centers when it fits and scrolls from the top when it doesn't,
  // so the sign-up form is never cut off on mobile. Grain is fixed to the viewport.
  <div
    className="fixed inset-0 overflow-y-auto"
    style={{ background: AUTH_HERO_GRADIENT }}
  >
    <div
      className="pointer-events-none fixed inset-0 opacity-[0.06] mix-blend-overlay"
      style={{ backgroundImage: GRAIN_URL }}
      aria-hidden="true"
    />
    <div className="relative flex min-h-full flex-col px-4 py-12">
      <div className="m-auto w-full max-w-sm space-y-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <img
            src={SylvaWordmark}
            alt="Sylva"
            className="h-9 w-auto"
          />
          {tagline && (
            <p className="max-w-xs text-sm leading-relaxed text-white/60">{tagline}</p>
          )}
        </div>
        {children}
      </div>
    </div>
  </div>
);
